import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import Axios from 'axios';
import restApi, { auth } from 'services/restApi';
import { getLanguage } from '@icp/i18n';
import { setUserProfile, getLogoutRedirectUrl } from '@icp/settings';
import { toMap, getSearchParams, deleteCookie, getCookie } from '@icp/utils';
import { isEqual } from 'lodash-es';

export const selectIsAuthing = (state) => state.auth.isAuthing;
export const selectUserPermissionMap = (state) => state.auth.userPermissionMap;
export const selectAuthError = (state) => state.auth.error;
export const selectUserProfile = (state) => state.auth.userProfile;
export const selectIsAdminUser = (state) => state.auth.userProfile?.isAdmin === 1;
export const selectLoginConfig = (state) => state.auth.loginConfig;
export const selectLoginSources = (state) => state.auth.loginSources;

export const AUTH_SUCCESS_REDIRECT_URL_STORAGE_KEY = 'AUTH_SUCCESS_REDIRECT_URL_C5793E6F47DF';

const restClient = Axios.create({
  baseURL: window.__bot_conf__?.axiosBaseURL,
  headers: {
    'Accept-Language': getLanguage(),
  },
});
restClient.interceptors.response.use((response) => response.data);

const fetchUserProfile = () =>
  restApi
    .get(`/user-management/api/user/get-user-info`)
    .then((userProfile) => {
      setUserProfile(userProfile);
      return userProfile;
    })
    .then(async (userProfile) => {
      const userId = userProfile.formUserDataId;
      if (userId) {
        const userData = await restApi.get(
          `/form/api/v2/form-entity-data/user-management-new/system-user-form/${userId}`,
        );
        const newUserProfile = { ...userProfile, avatar: userData.avatar?.resources?.[0]?.url };
        setUserProfile(newUserProfile);
        return newUserProfile;
      }
      return userProfile;
    })
    .catch(() => null);

const GONNA_REDIRECT = (() => {
  const err = Error('未登录');
  err.code = 'GONNA_REDIRECT';
  return err;
})();

export const fetchLoginConfig = createAsyncThunk(
  'auth/fetchLoginConfig',
  //
  async () => {
    const loginConfig = await restClient.get('/user-management/api/login/config');
    const loginSources = (loginConfig || []).sort((a, b) => a.order - b.order);
    return {
      loginConfig,
      loginSources,
    };
  },
);

export const loginViaSource = createAsyncThunk(
  'auth/loginViaSource',
  //
  async ({ token: loginSourceToken, fromLogin, navigate }, { dispatch, getState }) => {
    if (!selectLoginSources(getState())) {
      await dispatch(fetchLoginConfig());
    }

    const loginSources = selectLoginSources(getState());
    const loginSource = loginSources.find((x) => x.token === loginSourceToken);

    if (!loginSource) {
      throw Error(`Login source not found: ${loginSourceToken}`);
    }

    if (/^auth/i.test(loginSource.type)) {
      let newSP;

      if (fromLogin) {
        newSP = new URLSearchParams(window.location.search);
        newSP.set('auth', loginSourceToken);
      } else {
        newSP = new URLSearchParams();
        newSP.set('auth', loginSourceToken);
        newSP.set('from', window.location.href);
      }

      if (navigate) {
        navigate(`/login/basic?${newSP.toString()}`, { replace: true });
      } else {
        window.location.replace(`${window.ICP_PUBLIC_PATH}login/basic?${newSP.toString()}`);
      }

      throw GONNA_REDIRECT;
    }

    if (
      loginSource.type?.toLowerCase() === 'redirect-sso' ||
      loginSource.type?.toLowerCase() === 'saml2-sso'
    ) {
      const { from } = getSearchParams();
      const successRedirect = fromLogin ? from : window.location.href;

      const url = await restClient.get(`/user-management/api/login/redirect-url`, {
        params: { auth: loginSource.token },
      });

      sessionStorage.setItem(AUTH_SUCCESS_REDIRECT_URL_STORAGE_KEY, successRedirect || '');

      window.location.href = url;

      throw GONNA_REDIRECT;
    }

    throw Error(`Unknown login source type: ${loginSource.type}`);
  },
);

const redirect = (destination) => {
  const url = new URL(destination, window.location.origin);
  url.searchParams.set('from', window.location.href);
  window.location.replace(url.href);
};

export const checkAuthState = createAsyncThunk(
  'auth/checkAuthState',
  //
  async (payload, { dispatch, getState }) => {
    // 若localStorage中有token不重走授权流程
    const accessToken = auth.getAccessToken();
    const refreshToken = auth.getRefreshToken();
    if (auth.hasActiveAccessToken()) {
      const userProfile = selectUserProfile(getState()) || (await fetchUserProfile());
      // 已登录 (有token且没过期) 注册刷新任务
      auth.beginRefreshTokenTask();
      auth.restartIdleListener();
      return { accessToken, refreshToken, userProfile };
    }

    const logoutRedirectUrl = getLogoutRedirectUrl();
    if (logoutRedirectUrl) {
      redirect(logoutRedirectUrl);
      throw GONNA_REDIRECT;
    }

    if (!selectLoginSources(getState())) {
      await dispatch(fetchLoginConfig());
    }
    const loginSources = selectLoginSources(getState());

    const visibleLoginSources = loginSources.filter((source) => !source.hide);
    if (visibleLoginSources.length === 1) {
      const loginSource = visibleLoginSources[0];
      await dispatch(loginViaSource({ token: loginSource.token })).unwrap();
    }

    redirect(`${window.ICP_PUBLIC_PATH}login`);
    throw GONNA_REDIRECT;
  },
);

export const ssoLogin = createAsyncThunk(
  'auth/ssoLogin',
  //
  async (payload) => {
    const { payload: body, loginSourceToken } = payload;
    const data = await restApi.post('/user-management/api/login/ssoLogin', body, {
      params: { auth: loginSourceToken },
    });
    auth.setToken({
      access: data.accessToken,
      refresh: data.refreshToken,
    });
    auth.beginRefreshTokenTask();
    auth.restartIdleListener();
    const userProfile = await fetchUserProfile();
    return { ...data, userProfile };
  },
);

export const ssoLoginSuccessCallback = createAsyncThunk(
  'auth/ssoLoginSuccessCallback',
  //
  async () => {
    const accessToken = getCookie('access_token');
    const refreshToken = getCookie('refresh_token');
    deleteCookie('access_token');
    deleteCookie('refresh_token');

    auth.setToken({
      access: accessToken,
      refresh: refreshToken,
    });
    auth.beginRefreshTokenTask();
    auth.restartIdleListener();
    const userProfile = await fetchUserProfile();
    return { accessToken, refreshToken, userProfile };
  },
);

export const classicLogin = createAsyncThunk(
  'auth/classicLogin',
  async (payload, { rejectWithValue }) => {
    const { loginSourceToken, data: body, otpTools } = payload;

    try {
      const loginResult = await restApi
        .post(`/user-management/api/user/login`, body, {
          params: { auth: loginSourceToken },
        })
        .then(async (data) => {
          if (!data) throw Error();
          // normal
          if (data.accessToken) return data;
          // 2FA
          if (data.twoFactorAuth) {
            // first time, not bound yet
            if (data.twoFactorAuth.optAuthUrl) return otpTools.bind(data.twoFactorAuth);
            return otpTools.verify(data.twoFactorAuth);
          }
          // Fatal error: unknown login response
          throw Error();
        });

      if (!loginResult) throw Error();

      auth.setToken({
        access: loginResult.accessToken,
        refresh: loginResult.refreshToken,
      });
      auth.beginRefreshTokenTask();
      auth.restartIdleListener();
      const userProfile = await fetchUserProfile();
      return { userProfile };
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    loginConfig: null,
    loginSources: null,

    isAuthing: true,
    error: null,

    userProfile: null,
    userPermissions: [],
    userPermissionMap: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLoginConfig.rejected, (state) => {
        state.loginConfig = null;
        state.loginSources = [];
      })
      .addCase(fetchLoginConfig.fulfilled, (state, action) => {
        const { loginConfig, loginSources } = action.payload;
        state.loginConfig = loginConfig;
        state.loginSources = loginSources;
      })
      .addCase(checkAuthState.pending, (state) => {
        state.isAuthing = true;
        state.error = null;
      })
      .addCase(checkAuthState.rejected, (state, action) => {
        state.isAuthing = false;
        state.error = action.error;
        state.accessToken = null;
        state.refreshToken = null;
        state.userProfile = null;
        state.userPermissions = [];
        state.userPermissionMap = {};
      })
      .addCase(loginViaSource.rejected, (state, action) => {
        if (action.error?.code !== GONNA_REDIRECT.code) {
          console.warn(action.error);
        }
      })
      .addMatcher(
        isAnyOf(
          checkAuthState.fulfilled,
          classicLogin.fulfilled,
          ssoLogin.fulfilled,
          ssoLoginSuccessCallback.fulfilled,
        ),
        (state, action) => {
          const { userProfile } = action.payload;
          const userPermissions = userProfile?.permissions ?? [];

          state.isAuthing = false;
          state.error = null;

          if (!isEqual(state.userProfile, userProfile)) {
            const userPermissionMap = toMap(
              (x) => x.token,
              () => true,
            )(userPermissions);

            state.userProfile = userProfile;
            state.userPermissions = userPermissions;
            state.userPermissionMap = userPermissionMap;
          }
        },
      );
  },
});

// export const {} = authSlice.actions;

export default authSlice.reducer;
